/**
 * A fresh idempotency key: a UUID v4, the form every public write endpoint
 * validates its `requestId` against.
 *
 * `crypto.randomUUID()` exists only in a secure context — HTTPS or
 * `localhost`. Opened over plain HTTP (a phone on the LAN pointed at the dev
 * server, say) it is undefined, so the key is built from `getRandomValues`
 * instead, which works everywhere and is just as random.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * A one-way fingerprint of a request body, mixed with its key.
 *
 * What lets a form recognise "the same submission" after a reload without
 * keeping the body — and so the visitor's name, email and phone — in storage.
 * It is only ever compared, never read back.
 *
 * Mixing in the key means a given email does not produce the same value twice,
 * so a precomputed table of guesses is no use. It is a fast 53-bit hash
 * (cyrb53), not a cryptographic one: what it guards is an equality check in
 * the visitor's own tab, and it has to work synchronously and outside a secure
 * context, where `crypto.subtle` does not exist.
 */
export function fingerprint(key: string, body: unknown): string {
  const text = `${key}\u0000${JSON.stringify(body)}`;
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
